import SmoothScrollbars from '@rio-cloud/rio-uikit/components/smoothScrollbars/SmoothScrollbars';
import Fade from '@rio-cloud/rio-uikit/Fade';
import useEffectOnce from '@rio-cloud/rio-uikit/useEffectOnce';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { DEFAULT_ROUTE, TOUR_HANDLING_DETAILS, TOUR_HANDLING_ROUTE } from '../../common/constants';
import { PageNavigationBar } from '../../components/PageNavigationBar/PageNavigationBar';
import { TourCard } from '../../components/TourCard/TourCard';
import { DEFAULT_ACTIVE_TAB, TABS, type TourTab, TourTabs } from '../../components/TourTabs/TourTabs';
import { useAppDispatch, useAppSelector } from '../../configuration/setup/hooks';
import { useCategorizedTours } from '../../hooks/useCategorizedTours';
import { propagateRoutes, requestBoundingBox } from '../../features/livemonitorCommunication/MapRouteRendering';
import { selectPageDisplayed } from '../../store/app/appSelectors';
import { updatePage, updatePastTourFlag, updateTourHandlingId } from '../../store/app/appSlice';
import { useGetAssetDataQuery } from '../../store/facade/facadeApiV3';
import { useGetToursQuery } from '../../store/facade/facadeApiV3Enhanced';
import { selectLocale } from '../../store/lang/langSelectors';
import { selectSelectedAssetId } from '../../store/widget/widgetSelectors';
import { selectVehicleProfile } from '../../store/vehicleProfile/vehicleProfileSelectors';
import { selectItineraries, selectSelectedItinerary } from '../../store/tour/tourSelectors';
import classNames from 'classnames';

export const TourHandlingPage = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [activeTab, setActiveTab] = useState<TourTab>(DEFAULT_ACTIVE_TAB);
    const assetId = useAppSelector(selectSelectedAssetId);

    const { data: vehicleData } = useGetAssetDataQuery({ id: assetId ?? '' }, { skip: !assetId });
    const assetName = useAppSelector(selectVehicleProfile).assetLocked?.name;

    const { data: tours } = useGetToursQuery({ assetId: assetId ?? undefined }, { skip: !assetId });

    const currentPage = useAppSelector(selectPageDisplayed);
    const locale = useAppSelector(selectLocale);
    const showContent = currentPage === TOUR_HANDLING_ROUTE;
    const itineraries = useAppSelector(selectItineraries);
    const selectedItinerary = useAppSelector(selectSelectedItinerary);

    const currentPageRef = useRef(currentPage);
    currentPageRef.current = currentPage;

    const { onGoingTour, futureTours, pastTours } = useCategorizedTours(tours?.tours);

    useEffectOnce(() => {
        dispatch(updatePage(TOUR_HANDLING_ROUTE));
    });

    const handleTourClick = (tourId: string, bucket: 'past' | 'future' | 'ongoing' | 'none') => {
        dispatch(updatePastTourFlag(bucket !== 'future' && bucket !== 'ongoing'));
        dispatch(updateTourHandlingId(tourId));
        dispatch(updatePage(TOUR_HANDLING_DETAILS));
    };

    const handleClickGoBackNav = () => {
        dispatch(updatePage(DEFAULT_ROUTE));
        if (itineraries && itineraries.length > 0) {
            dispatch(propagateRoutes(itineraries, selectedItinerary ?? 0));
            requestBoundingBox(itineraries);
        }
    };

    const handleExitNav = () => {
        navigate(currentPageRef.current, { replace: false });
    };

    const vehicleStateClasses = vehicleData?.state?.ignition_on
        ? classNames('badge-success', 'badge-indicator-pinging')
        : classNames('badge-default');

    return (
        <Fade initial animationStyle={Fade.PAGE_BACK} show={showContent} onExitComplete={handleExitNav}>
            <div className='display-flex flex-column height-100vh' data-testid='tour-handling-page'>
                <PageNavigationBar title='intl-msg:smartRoute.tour.handling.title' navBack={handleClickGoBackNav} />
                <SmoothScrollbars>
                    <div className='display-flex flex-column padding-x-15' data-testid='tour-handling-layout'>
                        {assetName && (
                            <div
                                className='display-flex align-items-center column-gap-8 text-size-18 text-medium padding-top-15 padding-bottom-5'
                                data-testid='vehicle-name'
                            >
                                <span className='indicated-button'>
                                    <span className='rioglyph rioglyph-truck' style={{ fontSize: 24 }} />
                                    <span
                                        className={classNames('badge badge-indicator', vehicleStateClasses)}
                                        style={{ width: 8, height: 8, minWidth: 8, minHeight: 8 }}
                                    />
                                </span>
                                <span className='padding-left-5'>{assetName}</span>
                            </div>
                        )}
                        <div className='display-flex flex-column row-gap-15 padding-bottom-25'>
                            {onGoingTour && (
                                <TourCard
                                    itinerarySummary={onGoingTour.itinerarySummary}
                                    departureAddress={onGoingTour.departureAddress}
                                    arrivalAddress={onGoingTour.arrivalAddress}
                                    locale={locale}
                                    status={onGoingTour.status}
                                    isOnGoing
                                    onClick={() => onGoingTour.tourId && handleTourClick(onGoingTour.tourId, 'ongoing')}
                                />
                            )}
                            <TourTabs activeTab={activeTab} onTabChange={setActiveTab} />
                            {activeTab === TABS[0]
                                ? futureTours.map(tour => (
                                      <TourCard
                                          key={tour.tourId}
                                          locale={locale}
                                          itinerarySummary={tour.itinerarySummary}
                                          departureAddress={tour.departureAddress}
                                          arrivalAddress={tour.arrivalAddress}
                                          status={tour.status}
                                          onClick={() => tour.tourId && handleTourClick(tour.tourId, 'future')}
                                      />
                                  ))
                                : pastTours.map(tour => (
                                      <TourCard
                                          key={tour.tourId}
                                          locale={locale}
                                          itinerarySummary={tour.itinerarySummary}
                                          departureAddress={tour.departureAddress}
                                          arrivalAddress={tour.arrivalAddress}
                                          status={tour.status}
                                          onClick={() => tour.tourId && handleTourClick(tour.tourId, 'past')}
                                      />
                                  ))}
                        </div>
                    </div>
                </SmoothScrollbars>
            </div>
        </Fade>
    );
};
